/**
 * FemBe定期便 カスタム申し込みフロー
 * assets/fembe-subscription.js
 */
(function () {
  'use strict';

  const CFG = window.FEMBE_CONFIG;

  /* ── State ─────────────────────────────── */
  const state = {
    step: 1,
    theme: 'karada',
    planId: CFG.sellingPlans['karada'][30],
    planDays: 30,
    items: [],        // [{ variantId, productId, quantity, price, title, img }]
    upsells: [],      // 同上（STEP3追加分）
    step2Loaded: false,
    step3Loaded: false,
  };

  let updateInlineFooter = null;
  let modalCloseRefresh = null; // モーダルを閉じた時にグリッドの数量表示を更新するコールバック
  const productCache = {}; // productId → productData（詳細モーダル用）

  /* ── Helpers ────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
  // products.json の価格は文字列の円（例: "1481" = ¥1,481）
  const fmt = (yen) => Math.round(+yen).toLocaleString('ja-JP') + '円';

  function allItems() {
    return [...state.items, ...state.upsells];
  }

  function getQty(variantId, pool = allItems()) {
    return pool.filter(i => i.variantId === variantId).reduce((s, i) => s + i.quantity, 0);
  }

  // 同じバリエーションがSTEP2/STEP3両方の選択肢に出てくる場合、
  // 在庫上限は両ステップ合計で判定する必要があるため、
  // 「もう一方のプール（stateの反対側）で既に使われている数量」を返す
  function otherPoolQty(variantId, pool) {
    return getQty(variantId, allItems()) - getQty(variantId, pool);
  }

  // プールをまたいだ合計を考慮した実質的な在庫上限を返す
  function effectiveMax(max, variantId, pool) {
    if (max === Infinity) return Infinity;
    return Math.max(0, max - otherPoolQty(variantId, pool));
  }

  function getProductQty(productId, pool = allItems()) {
    return pool.filter(i => i.productId === productId).reduce((s, i) => s + i.quantity, 0);
  }

  function getGroupQty(groupId, pool, groups) {
    const ids = new Set(
      Object.entries(groups).filter(([, g]) => g.groupId === groupId).map(([id]) => +id)
    );
    return pool.filter(i => ids.has(i.productId)).reduce((s, i) => s + i.quantity, 0);
  }

  function addItem(pool, item) {
    const ex = pool.find(i => i.variantId === item.variantId);
    if (ex) { ex.quantity++; }
    else { pool.push({ ...item, quantity: 1 }); }
  }

  function removeItem(pool, variantId) {
    const idx = pool.findIndex(i => i.variantId === variantId);
    if (idx < 0) return;
    pool[idx].quantity--;
    if (pool[idx].quantity <= 0) pool.splice(idx, 1);
  }

  function setItemQty(pool, variantId, newQty, itemData) {
    const idx = pool.findIndex(i => i.variantId === variantId);
    if (newQty <= 0) {
      if (idx >= 0) pool.splice(idx, 1);
    } else if (idx >= 0) {
      pool[idx].quantity = newQty;
    } else if (itemData) {
      pool.push({ ...itemData, quantity: newQty });
    }
  }

  // 在庫管理設定から上限を返す（在庫管理なし or 継続販売は Infinity）
  function inventoryMax(invQty, invMgmt, invPolicy, avail) {
    if (avail === 'false' || avail === false) return 0;
    if (invMgmt === 'shopify' && invPolicy !== 'continue') return Math.max(0, +invQty || 0);
    return Infinity;
  }

  // /products.json は在庫関連フィールド（inventory_quantity等）を含まないため、
  // Liquidが埋め込んだ window.FEMBE_INVENTORY（fembe-subscription-page.liquid）から取得する。
  // Liquid側は collection.products の取得上限（既定250件）を超えると対象商品が
  // 登録されないことがあるため、その場合は products.json（全ページ取得済み）の
  // variant.available を渡してもらい、それを使って「在庫なし」誤表示を防ぐ
  // （数量上限チェックのみ効かなくなるが、販売自体をブロックしない安全側の挙動）。
  function getInv(variantId, fallbackAvailable) {
    const found = (window.FEMBE_INVENTORY || {})[variantId];
    if (found) return found;
    return { qty: 0, mgmt: '', policy: 'deny', available: fallbackAvailable !== undefined ? fallbackAvailable : false };
  }

  function showInventoryMsg(msgEl, max) {
    if (!msgEl) return;
    msgEl.textContent = `このアイテムは${max}個しかカートに追加することができません。`;
  }

  function clearInventoryMsg(msgEl) {
    if (!msgEl) return;
    msgEl.textContent = '';
  }

  // 在庫上限に達したら+ボタンを押せなくする
  function updatePlusButtonState(btn, currentQty, max) {
    if (!btn) return;
    const atMax = currentQty >= max;
    btn.disabled = atMax;
    btn.classList.toggle('is-disabled', atMax);
  }

  /* ── Subtotal ───────────────────────────── */
  /* ── 内訳：商品グループ化レンダリング ── */
  function renderBreakdownItems() {
    const fembeImg = 'https://cdn.shopify.com/s/files/1/0688/5303/9217/files/products-image-products_01.png';

    // FemBe固定アイテム
    const fembeHtml = `
      <div class="fembe-sub__breakdown-item">
        <img src="${fembeImg}" alt="FemBe" class="fembe-sub__breakdown-item-img">
        <div class="fembe-sub__breakdown-item-body">
          <p class="fembe-sub__vendor">ユニ・チャーム</p>
          <p class="fembe-sub__breakdown-item-name">FemBe（サプリメント）</p>
          <p class="fembe-sub__breakdown-item-meta">${fmt(CFG.fembePrice)}</p>
          <div class="fembe-sub__breakdown-item-variants">
            <div class="fembe-sub__breakdown-variant-row">
              <span>内容量：60粒入り（約30日分） × 1</span>
            </div>
          </div>
          <p class="fembe-sub__breakdown-item-total">${fmt(CFG.fembePrice)}</p>
        </div>
      </div>`;

    // 選択商品をproductIdでグループ化
    const groups = {};
    allItems().forEach(i => {
      const pid = i.productId;
      if (!groups[pid]) groups[pid] = { productTitle: i.productTitle || i.title, img: i.img, variants: [] };
      groups[pid].variants.push(i);
    });

    const groupHtml = Object.values(groups).map(g => {
      const variantRows = g.variants.map(v => {
        const label = v.variantLabel || v.title;
        return `
          <p class="fembe-sub__breakdown-item-meta">${fmt(v.price)}</p>
          <div class="fembe-sub__breakdown-variant-row">
            <span>${label} × ${v.quantity}</span>
          </div>`;
      }).join('');
      const groupTotal = g.variants.reduce((s, v) => s + (+v.price * v.quantity), 0);
      return `
        <div class="fembe-sub__breakdown-item">
          <img src="${g.img || ''}" alt="${g.productTitle}" class="fembe-sub__breakdown-item-img">
          <div class="fembe-sub__breakdown-item-body">
            <p class="fembe-sub__vendor">ユニ・チャーム</p>
            <p class="fembe-sub__breakdown-item-name">${g.productTitle}</p>
            <div class="fembe-sub__breakdown-item-variants">${variantRows}</div>
            <p class="fembe-sub__breakdown-item-total">${fmt(groupTotal)}</p>
          </div>
        </div>`;
    }).join('');

    return fembeHtml + groupHtml;
  }

  function updateSubtotal() {
    const subtotalBeforeDiscount = CFG.fembePrice + allItems().reduce((s, i) => s + +i.price * i.quantity, 0);
    const total = subtotalBeforeDiscount;
    const count = 1 + allItems().reduce((s, i) => s + i.quantity, 0);

    // 合計・件数
    $$('.js-subtotal').forEach(el => (el.innerHTML = fmt(total) + '<span class="fembe-sub__tax-note">（税込）</span>'));
    $$('.js-item-count').forEach(el => (el.textContent = `${count}個のアイテム`));

    // 内訳パネル（STEP2/3両方更新）
    $$('.js-subtotal-before-discount').forEach(el => (el.textContent = fmt(subtotalBeforeDiscount)));

    // 内訳：商品でグループ化して表示（STEP2/3両方更新）
    $$('.js-breakdown-items').forEach(el => (el.innerHTML = renderBreakdownItems()));

    // 送料無料メッセージ（追従・インライン共通）
    const isFree = total >= CFG.freeShippingThreshold;
    const shippingText = isFree ? '無料' : 'チェックアウトで計算致します';
    $$('.js-shipping-note, .js-shipping-note-inline').forEach(el => {
      el.textContent = shippingText;
      // 無料時は金額と同サイズ、長文時は小さめ
      el.style.fontSize = isFree ? '13px' : '11px';
      el.style.color = isFree ? 'var(--fembe-text)' : '';
    });

    // STEP4インライン内訳
    const inlineItems = $('.js-breakdown-items-inline');
    if (inlineItems) {
      inlineItems.innerHTML = renderBreakdownItems();
    }
    const inlineBefore = $('.js-subtotal-before-discount-inline');
    const inlineTotal = $('.js-subtotal-inline');
    const inlineCount = $('.js-item-count-inline');
    if (inlineBefore) inlineBefore.textContent = fmt(subtotalBeforeDiscount);
    if (inlineTotal) inlineTotal.textContent = fmt(total);
    if (inlineCount) inlineCount.textContent = `${count}個のアイテム`;

    // 送料無料まであと（STEP3）
    const msg = $('.js-free-shipping-msg');
    if (msg) {
      const diff = CFG.freeShippingThreshold - total;
      msg.innerHTML = diff > 0
        ? `あと${fmt(diff)}で送料が無料になります！<br>商品を追加しませんか？`
        : '送料無料です！';
    }

    // STEP2 次へボタン：商品が1つ以上あれば活性化
    const step2Next = $('[data-step-panel="2"] .fembe-sub__btn-next');
    if (step2Next) step2Next.disabled = state.items.length === 0;
  }

  /* ── Footer actions per step ────────────── */
  const FOOTER_ACTIONS = {
    2: `<button class="fembe-sub__btn-next" data-next="3">次へ</button>
        <button class="fembe-sub__btn-back" data-back="1">戻る</button>`,
    3: `<button class="fembe-sub__btn-next" data-next="4">次へ</button>
        <button class="fembe-sub__btn-back" data-back="2">戻る</button>`,
  };

  function updateFooter(step) {
    // 追従フッターは使用しない（全STEP埋め込み式）
    const footer = $('#sticky-footer');
    if (footer) footer.classList.add('is-hidden');
  }

  /* ── Step navigation ────────────────────── */
  const STEP_LABELS = {
    1: 'セットのテーマを選んでください。',
    2: 'アイテムを選ぶ',
    3: 'ちょい足し商品はいかがですか？',
    4: 'ご注文サイクルを選ぶ',
  };

  function goTo(step) {
    $$('.fembe-sub__step').forEach(el => el.classList.add('is-hidden'));
    $(`[data-step-panel="${step}"]`).classList.remove('is-hidden');

    $$('.fembe-sub__progress-step').forEach(el => {
      const s = +el.dataset.step;
      el.classList.toggle('is-active', s === step);
      el.classList.toggle('is-done', s < step);
    });

    const headerLabel = $('#js-header-step-label');
    if (headerLabel) headerLabel.textContent = STEP_LABELS[step] || '';

    const header = $('.fembe-sub__header');
    if (header) header.classList.toggle('is-step-content', step > 1);

    state.step = step;
    if (step === 2 && !state.step2Loaded) loadProducts('step2');
    if (step === 3 && !state.step3Loaded) loadProducts('step3');
    updateSubtotal();
    updateFooter(step);
    // 内訳パネルをすべて閉じてリセット
    $$('.js-inline-breakdown').forEach(p => p.classList.add('is-hidden'));
    $$('.js-inline-toggle-icon').forEach(i => i.classList.remove('is-open'));
    // STEP4の内訳は初期状態でオープン
    if (step === 4) {
      const step4 = $('[data-step-panel="4"]');
      const bd = step4?.querySelector('.js-inline-breakdown');
      const ic = step4?.querySelector('.js-inline-toggle-icon');
      if (bd) bd.classList.remove('is-hidden');
      if (ic) ic.classList.add('is-open');
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (updateInlineFooter) updateInlineFooter();
  }

  /* ── Product loader ─────────────────────── */
  // 除外ルール
  const EXCLUDE_TYPES = ['セット商品'];
  const EXCLUDE_HANDLES = ['fembe-supplement', '750002', 'セットサンプル'];

  function filterByTag(products, stepTag) {
    return products.filter(p => {
      if (EXCLUDE_TYPES.includes(p.product_type)) return false;
      if (EXCLUDE_HANDLES.includes(p.handle)) return false;
      if (p.handle.startsWith('test')) return false;
      // タグを配列化して完全一致チェック
      const tagsArr = typeof p.tags === 'string'
        ? p.tags.split(',').map(t => t.trim())
        : (p.tags || []);
      if (stepTag && !tagsArr.includes(stepTag)) return false;
      return true;
    });
  }

  // window.FEMBE_VARIANT_GROUPS の products は「いずれかのstepタグを持つ」条件で
  // Liquid側が構築しているため、あるテーマにのみタグ付けされた商品が別テーマの
  // グリッドでもバリエーションとして出てきてしまう。表示直前に、現在の
  // stepKey/state.themeに一致するタグを持つ商品だけへ絞り込む。
  function filterGroupProductsForStep(groupProducts, stepKey) {
    const prefix = stepKey === 'step3' ? 'step3' : 'step2';
    const tag = `${prefix}-${state.theme}`;
    return (groupProducts || []).filter(gp => Array.isArray(gp.tags) && gp.tags.includes(tag));
  }

  // 選択テーマのselling plan IDが商品に含まれるか確認
  // /products.json・/products/<handle>.json はどちらもselling_plan_groupsを
  // 含まないため、Liquidが埋め込んだwindow.FEMBE_SELLING_PLAN_IDS（実際に
  // 登録済みの販売プランID一覧）と突き合わせる。
  function verifySellingPlan(product, themeKey) {
    const themePlanIds = Object.values(CFG.sellingPlans[themeKey] || {});
    if (!themePlanIds.length) {
      console.warn(`[FemBe] verifySellingPlan: themeKey="${themeKey}" のプランIDが未設定 → 通過`);
      return true;
    }

    const productPlanIds = (window.FEMBE_SELLING_PLAN_IDS || {})[product.id] || [];
    const matched = themePlanIds.some(id => productPlanIds.includes(id));
    console.log(
      `[FemBe] verifySellingPlan [${product.handle}] theme=${themeKey}`,
      `\n  テーマのplans: ${JSON.stringify(themePlanIds)}`,
      `\n  商品のplans:   ${JSON.stringify(productPlanIds)}`,
      `\n  → ${matched ? '✅ 一致' : '❌ 不一致（除外）'}`
    );
    return matched;
  }

  // /products.json はtagパラメータを実際には無視するため（クライアント側で
  // filterByTagするしかない）、limitで切られた1ページ目だけだと店舗の商品数が
  // 多い場合に対象商品が取得すらされないことがある。全ページ取得する。
  const PRODUCTS_JSON_PAGE_LIMIT = 250; // このエンドポイントの最大値
  const PRODUCTS_JSON_MAX_PAGES = 20; // 安全上限（最大5000件）

  let allProductsPromise = null; // STEP2/STEP3・テーマ切替をまたいで再利用するキャッシュ

  async function fetchAllProducts() {
    if (allProductsPromise) return allProductsPromise;
    allProductsPromise = (async () => {
      const all = [];
      for (let page = 1; page <= PRODUCTS_JSON_MAX_PAGES; page++) {
        const url = `/products.json?limit=${PRODUCTS_JSON_PAGE_LIMIT}&page=${page}`;
        const res = await fetch(url);
        const data = await res.json();
        const pageProducts = data.products || [];
        all.push(...pageProducts);
        if (pageProducts.length < PRODUCTS_JSON_PAGE_LIMIT) break;
      }
      return all;
    })();
    return allProductsPromise;
  }

  async function loadProducts(stepKey) {
    const container = $(`#${stepKey}-products`);
    const prefix = stepKey === 'step2' ? 'step2' : 'step3';
    const tag = `${prefix}-${state.theme}`;
    let products = [];

    try {
      console.group(`[FemBe] loadProducts — ${stepKey} / theme: ${state.theme}`);
      console.log('① API: /products.json を全ページ取得');

      const raw = await fetchAllProducts();
      console.log(`② API response: ${raw.length}件`, raw.map(p => ({
        handle: p.handle,
        tags: p.tags,
      })));

      // タグによるフィルター
      const tagMatched = filterByTag(raw, tag);
      console.log(`③ タグフィルター後: ${tagMatched.length}件`, tagMatched.map(p => p.handle));

      // タグが付いていても選択中テーマの販売プランに未登録の商品は購入できないため除外する
      products = tagMatched.filter(p => verifySellingPlan(p, state.theme));
      console.log(`④ 販売プラン検証後（最終表示）: ${products.length}件`, products.map(p => p.handle));
      console.log('⑤ 在庫データ確認（先頭3件・FEMBE_INVENTORY由来）:',
        products.slice(0, 3).flatMap(p => p.variants.map(v => {
          const inv = getInv(v.id);
          return `${p.handle}#${v.id}: qty=${inv.qty}, mgmt=${inv.mgmt}, policy=${inv.policy}, available=${inv.available}`;
        }))
      );
      console.groupEnd();
    } catch (e) {
      console.error('[FemBe] loadProducts error:', e);
      console.groupEnd();
    }

    products.forEach(p => { productCache[p.id] = p; });
    renderProductGrid(container, products, stepKey);
    if (stepKey === 'step2') state.step2Loaded = true;
    else state.step3Loaded = true;
    // 商品レンダリング後にレイアウトが変わるため、フッター位置を再計算
    requestAnimationFrame(() => { if (updateInlineFooter) updateInlineFooter(); });
  }

  /* ── Product grid renderer ──────────────── */
  function renderProductGrid(container, products, stepKey) {
    if (!products.length) {
      container.innerHTML = '<p class="fembe-sub__empty">商品がありません</p>';
      return;
    }

    const pool = stepKey === 'step3' ? state.upsells : state.items;
    const groups = window.FEMBE_VARIANT_GROUPS || {};
    const renderedGroupIds = new Set();
    const cards = [];

    products.forEach(p => {
      const groupData = groups[p.id];
      if (groupData) {
        if (renderedGroupIds.has(groupData.groupId)) return;
        renderedGroupIds.add(groupData.groupId);
        const gProducts = filterGroupProductsForStep(groupData.products, stepKey);

        // stepタグ検証等でグループの有効な商品が0件になった場合は表示しない
        if (gProducts.length === 0) return;

        // 有効な商品が1件のみならモーダルを挟まず単一商品と同様にカード上で数量変更させる
        if (gProducts.length === 1) {
          const gp = gProducts[0];
          const invData = getInv(gp.variantId, gp.available);
          const qty = getQty(gp.variantId, pool);
          const img = gp.img || p.images[0]?.src || '';
          const label = gp.label || gp.title;
          const gridMax = effectiveMax(
            inventoryMax(invData.qty, invData.mgmt, invData.policy, invData.available),
            gp.variantId,
            pool
          );
          const gridAtMax = qty >= gridMax;

          cards.push(`
            <div class="fembe-sub__product-card" data-product-id="${gp.productId}">
              <div class="fembe-sub__product-img">
                <img src="${img}" alt="${label}" loading="lazy">
              </div>
              <div class="fembe-sub__product-meta">
                <p class="fembe-sub__vendor">${p.vendor}</p>
                <p class="fembe-sub__name">${label}</p>
                <p class="fembe-sub__price">${fmt(gp.price)}</p>
              </div>
              <div class="fembe-sub__product-ctrl">
                <div class="fembe-sub__qty-row">
                  <button class="fembe-sub__qty-btn js-minus"
                    data-variant-id="${gp.variantId}" data-product-id="${gp.productId}" data-step-key="${stepKey}">−</button>
                  <input type="number" class="fembe-sub__qty-val fembe-sub__qty-input js-qty-val js-grid-qty-input"
                    value="${qty}" min="0"
                    data-variant-id="${gp.variantId}" data-product-id="${gp.productId}"
                    data-price="${gp.price}" data-title="${label}" data-img="${img}"
                    data-inventory="${invData.qty || 0}"
                    data-inv-mgmt="${invData.mgmt || ''}"
                    data-inv-policy="${invData.policy || ''}"
                    data-available="${invData.available}"
                    ${!invData.available ? 'disabled' : ''}>
                  <button class="fembe-sub__qty-btn js-plus${gridAtMax ? ' is-disabled' : ''}"
                    data-variant-id="${gp.variantId}" data-price="${gp.price}"
                    data-product-id="${gp.productId}" data-title="${label}"
                    data-img="${img}" data-step-key="${stepKey}"
                    data-inventory="${invData.qty || 0}"
                    data-inv-mgmt="${invData.mgmt || ''}"
                    data-inv-policy="${invData.policy || ''}"
                    data-available="${invData.available}"
                    ${gridAtMax ? 'disabled' : ''}>＋</button>
                </div>
                <p class="fembe-sub__inventory-msg js-inventory-msg">${!invData.available ? '在庫なし' : ''}</p>
              </div>
            </div>`);
          return;
        }

        const groupQty = getGroupQty(groupData.groupId, pool, groups);
        const img = gProducts[0]?.img || p.images[0]?.src || '';
        const groupMinPrice = Math.min(...gProducts.map(gp => +gp.price));
        cards.push(`
          <div class="fembe-sub__product-card" data-product-id="${p.id}" data-group-id="${groupData.groupId}">
            <div class="fembe-sub__product-img">
              <img src="${img}" alt="${groupData.groupName}" loading="lazy">
            </div>
            <div class="fembe-sub__product-meta">
              <p class="fembe-sub__vendor">${p.vendor}</p>
              <p class="fembe-sub__name">${groupData.groupName}</p>
              <p class="fembe-sub__price">${fmt(groupMinPrice)}〜</p>
            </div>
            <div class="fembe-sub__product-ctrl">
              ${(function() {
                const gOpts = (p.options || []).map(o => o.name).filter(n => n !== 'Title');
                const gLabel = gOpts.length > 0 ? gOpts.join('・') + '選択' : 'バリエーション選択';
                return `<button class="fembe-sub__variant-btn js-group-btn"
                  data-group-id="${groupData.groupId}" data-step-key="${stepKey}"
                  data-option-label="${gLabel}">
                  ${groupQty > 0 ? `${gLabel} (${groupQty})` : gLabel}
                </button>`;
              })()}
            </div>
          </div>`);
        return;
      }

      const v = p.variants[0];
      const invData = getInv(v.id, v.available);
      const hasVar = p.variants.length > 1;
      const qty = getProductQty(p.id, pool);
      const img = p.images[0]?.src || '';
      const optionNames = (p.options || []).map(o => o.name).filter(n => n !== 'Title');
      const optionLabel = optionNames.length > 0 ? optionNames.join('・') + '選択' : 'バリエーション選択';
      const gridMax = effectiveMax(
        inventoryMax(invData.qty, invData.mgmt, invData.policy, invData.available),
        v.id,
        pool
      );
      const gridAtMax = qty >= gridMax;

      cards.push(`
        <div class="fembe-sub__product-card" data-product-id="${p.id}">
          <div class="fembe-sub__product-img">
            <img src="${img}" alt="${p.title}" loading="lazy">
          </div>
          <div class="fembe-sub__product-meta">
            <p class="fembe-sub__vendor">${p.vendor}</p>
            <p class="fembe-sub__name">${p.title}</p>
            <p class="fembe-sub__price">${fmt(v.price)}</p>
          </div>
          <div class="fembe-sub__product-ctrl">
            ${hasVar
              ? `<button class="fembe-sub__variant-btn js-variant-btn"
                   data-product-id="${p.id}" data-step-key="${stepKey}"
                   data-option-label="${optionLabel}">
                   ${qty > 0 ? `${optionLabel} (${qty})` : optionLabel}
                 </button>`
              : `<div class="fembe-sub__qty-row">
                   <button class="fembe-sub__qty-btn js-minus"
                     data-variant-id="${v.id}" data-product-id="${p.id}" data-step-key="${stepKey}">−</button>
                   <input type="number" class="fembe-sub__qty-val fembe-sub__qty-input js-qty-val js-grid-qty-input"
                     value="${qty}" min="0"
                     data-variant-id="${v.id}" data-product-id="${p.id}"
                     data-price="${v.price}" data-title="${p.title}" data-img="${img}"
                     data-inventory="${invData.qty || 0}"
                     data-inv-mgmt="${invData.mgmt || ''}"
                     data-inv-policy="${invData.policy || ''}"
                     data-available="${invData.available}"
                     ${!invData.available ? 'disabled' : ''}>
                   <button class="fembe-sub__qty-btn js-plus${gridAtMax ? ' is-disabled' : ''}"
                     data-variant-id="${v.id}" data-price="${v.price}"
                     data-product-id="${p.id}" data-title="${p.title}"
                     data-img="${img}" data-step-key="${stepKey}"
                     data-inventory="${invData.qty || 0}"
                     data-inv-mgmt="${invData.mgmt || ''}"
                     data-inv-policy="${invData.policy || ''}"
                     data-available="${invData.available}"
                     ${gridAtMax ? 'disabled' : ''}>＋</button>
                 </div>
                 <p class="fembe-sub__inventory-msg js-inventory-msg">${!invData.available ? '在庫なし' : ''}</p>`
            }
          </div>
        </div>`);
    });

    container.innerHTML = cards.join('');
    bindGridEvents(container, pool);
  }

  /* ── Grid event binding ─────────────────── */
  function bindGridEvents(container, pool) {
    $$(`.js-plus`, container).forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const variantId = +btn.dataset.variantId;
        const max = inventoryMax(btn.dataset.inventory, btn.dataset.invMgmt, btn.dataset.invPolicy, btn.dataset.available);
        const effMax = effectiveMax(max, variantId, pool);
        const currentQty = getQty(variantId, pool);
        const msgEl = btn.closest('.fembe-sub__product-ctrl')?.querySelector('.js-inventory-msg');
        if (currentQty >= effMax) {
          showInventoryMsg(msgEl, effMax);
          updatePlusButtonState(btn, currentQty, effMax);
          return;
        }
        clearInventoryMsg(msgEl);
        addItem(pool, {
          variantId,
          productId: +btn.dataset.productId,
          price: +btn.dataset.price,
          title: btn.dataset.title,
          productTitle: btn.dataset.productTitle,
          variantLabel: btn.dataset.variantLabel,
          img: btn.dataset.img,
        });
        updatePlusButtonState(btn, getQty(variantId, pool), effectiveMax(max, variantId, pool));
        refreshQtyInGrid(container, +btn.dataset.productId, pool);
        updateSubtotal();
      });
    });

    $$(`.js-minus`, container).forEach(btn => {
      btn.addEventListener('click', () => {
        const variantId = +btn.dataset.variantId;
        removeItem(pool, variantId);
        const row = btn.closest('.fembe-sub__qty-row');
        const plusBtn = row?.querySelector('.js-plus');
        const max = plusBtn
          ? inventoryMax(plusBtn.dataset.inventory, plusBtn.dataset.invMgmt, plusBtn.dataset.invPolicy, plusBtn.dataset.available)
          : Infinity;
        updatePlusButtonState(plusBtn, getQty(variantId, pool), effectiveMax(max, variantId, pool));
        const msgEl = btn.closest('.fembe-sub__product-ctrl')?.querySelector('.js-inventory-msg');
        clearInventoryMsg(msgEl);
        refreshQtyInGrid(container, +btn.dataset.productId, pool);
        updateSubtotal();
      });
    });

    $$(`.js-variant-btn`, container).forEach(btn => {
      btn.addEventListener('click', () => {
        openVariantModal(+btn.dataset.productId, btn.dataset.stepKey);
      });
    });

    $$(`.js-group-btn`, container).forEach(btn => {
      btn.addEventListener('click', () => {
        const groupId = btn.dataset.groupId;
        const gs = window.FEMBE_VARIANT_GROUPS || {};
        const groupData = Object.values(gs).find(g => g.groupId === groupId);
        if (!groupData) return;
        const stepKeyForModal = btn.dataset.stepKey;
        const filteredProducts = filterGroupProductsForStep(groupData.products, stepKeyForModal);
        if (filteredProducts.length) openGroupModal({ ...groupData, products: filteredProducts }, stepKeyForModal);
      });
    });

    $$(`.js-grid-qty-input`, container).forEach(input => {
      // フォーカス中のホイール操作でネイティブのスピナーが暴走しないようにする
      input.addEventListener('wheel', (e) => {
        if (document.activeElement === input) e.preventDefault();
      }, { passive: false });

      // リアルタイムバリデーション（入力のたびに在庫上限でクランプ）
      input.addEventListener('input', () => {
        const variantId = +input.dataset.variantId;
        const max = inventoryMax(input.dataset.inventory, input.dataset.invMgmt, input.dataset.invPolicy, input.dataset.available);
        const effMax = effectiveMax(max, variantId, pool);
        const val = parseInt(input.value, 10);
        const msgEl = input.closest('.fembe-sub__product-ctrl')?.querySelector('.js-inventory-msg');
        if (!isNaN(val) && val > effMax) {
          showInventoryMsg(msgEl, effMax);
          input.value = effMax;
        } else {
          clearInventoryMsg(msgEl);
        }
      });

      input.addEventListener('change', () => {
        const variantId = +input.dataset.variantId;
        const max = inventoryMax(input.dataset.inventory, input.dataset.invMgmt, input.dataset.invPolicy, input.dataset.available);
        const effMax = effectiveMax(max, variantId, pool);
        let newQty = Math.max(0, parseInt(input.value, 10) || 0);
        const msgEl = input.closest('.fembe-sub__product-ctrl')?.querySelector('.js-inventory-msg');
        if (newQty > effMax) {
          newQty = effMax;
          showInventoryMsg(msgEl, effMax);
        } else {
          clearInventoryMsg(msgEl);
        }
        input.value = newQty;
        const productId = +input.dataset.productId;
        setItemQty(pool, variantId, newQty, {
          variantId,
          productId,
          price: +input.dataset.price,
          title: input.dataset.title,
          img: input.dataset.img,
        });
        const plusBtn = input.closest('.fembe-sub__qty-row')?.querySelector('.js-plus');
        updatePlusButtonState(plusBtn, newQty, effMax);
        updateSubtotal();
      });
    });
  }

  function refreshQtyInGrid(container, productId, pool) {
    const card = container.querySelector(`[data-product-id="${productId}"]`);
    if (!card) return;
    const groupId = card.dataset.groupId;
    if (groupId) {
      const gs = window.FEMBE_VARIANT_GROUPS || {};
      const totalQty = getGroupQty(groupId, pool, gs);
      const btn = card.querySelector('.js-group-btn');
      if (btn) {
        const optionLabel = btn.dataset.optionLabel || 'バリエーション選択';
        btn.textContent = totalQty > 0 ? `${optionLabel} (${totalQty})` : optionLabel;
      }
      return;
    }
    const qty = getProductQty(productId, pool);
    const qtyEl = card.querySelector('.js-qty-val');
    if (qtyEl) {
      if (qtyEl.tagName === 'INPUT') qtyEl.value = qty;
      else qtyEl.textContent = qty;
    }
    const varBtn = card.querySelector('.js-variant-btn');
    if (varBtn) {
      const optionLabel = varBtn.dataset.optionLabel || 'バリエーション選択';
      varBtn.textContent = qty > 0 ? `${optionLabel} (${qty})` : optionLabel;
    }
  }

  /* ── Modal qty event binding (shared) ───────── */
  function bindModalQtyEvents(content, pool) {
    $$('.js-modal-plus', content).forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const variantId = +btn.dataset.variantId;
        const max = inventoryMax(btn.dataset.inventory, btn.dataset.invMgmt, btn.dataset.invPolicy);
        const effMax = effectiveMax(max, variantId, pool);
        const currentQty = getQty(variantId, pool);
        const row = btn.closest('.fembe-sub__variant-row');
        const msgEl = row.querySelector('.js-modal-inventory-msg');
        if (currentQty >= effMax) {
          showInventoryMsg(msgEl, effMax);
          updatePlusButtonState(btn, currentQty, effMax);
          return;
        }
        clearInventoryMsg(msgEl);
        addItem(pool, {
          variantId,
          productId: +btn.dataset.productId,
          price: +btn.dataset.price,
          title: btn.dataset.title,
          productTitle: btn.dataset.productTitle,
          variantLabel: btn.dataset.variantLabel,
          img: btn.dataset.img,
        });
        const newQty = getQty(variantId, pool);
        row.querySelector('.fembe-sub__qty-val').value = newQty;
        const minusBtn = row.querySelector('.js-modal-minus');
        if (minusBtn && newQty > 0) { minusBtn.disabled = false; minusBtn.classList.remove('is-disabled'); }
        updatePlusButtonState(btn, newQty, effectiveMax(max, variantId, pool));
        updateSubtotal();
      });
    });

    $$('.js-modal-minus', content).forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const variantId = +btn.dataset.variantId;
        removeItem(pool, variantId);
        const row = btn.closest('.fembe-sub__variant-row');
        const newQty = getQty(variantId, pool);
        row.querySelector('.fembe-sub__qty-val').value = newQty;
        if (newQty === 0) { btn.disabled = true; btn.classList.add('is-disabled'); }
        const plusBtn = row.querySelector('.js-modal-plus');
        if (plusBtn) {
          const max = inventoryMax(plusBtn.dataset.inventory, plusBtn.dataset.invMgmt, plusBtn.dataset.invPolicy);
          updatePlusButtonState(plusBtn, newQty, effectiveMax(max, variantId, pool));
        }
        clearInventoryMsg(row.querySelector('.js-modal-inventory-msg'));
        updateSubtotal();
      });
    });

    $$('.js-modal-qty-input', content).forEach(input => {
      // フォーカス中のホイール操作でネイティブのスピナーが暴走しないようにする
      input.addEventListener('wheel', (e) => {
        if (document.activeElement === input) e.preventDefault();
      }, { passive: false });

      input.addEventListener('input', () => {
        const variantId = +input.dataset.variantId;
        const max = inventoryMax(input.dataset.inventory, input.dataset.invMgmt, input.dataset.invPolicy);
        const effMax = effectiveMax(max, variantId, pool);
        const val = parseInt(input.value, 10);
        const row = input.closest('.fembe-sub__variant-row');
        const msgEl = row?.querySelector('.js-modal-inventory-msg');
        if (!isNaN(val) && val > effMax) {
          showInventoryMsg(msgEl, effMax);
          input.value = effMax;
        } else {
          clearInventoryMsg(msgEl);
        }
      });

      input.addEventListener('change', () => {
        const variantId = +input.dataset.variantId;
        const max = inventoryMax(input.dataset.inventory, input.dataset.invMgmt, input.dataset.invPolicy);
        const effMax = effectiveMax(max, variantId, pool);
        let newQty = Math.max(0, parseInt(input.value, 10) || 0);
        const row = input.closest('.fembe-sub__variant-row');
        const msgEl = row.querySelector('.js-modal-inventory-msg');
        if (newQty > effMax) { newQty = effMax; showInventoryMsg(msgEl, effMax); }
        else { clearInventoryMsg(msgEl); }
        input.value = newQty;
        const plusBtn = row.querySelector('.js-modal-plus');
        setItemQty(pool, variantId, newQty, plusBtn ? {
          variantId: +plusBtn.dataset.variantId,
          productId: +plusBtn.dataset.productId,
          price: +plusBtn.dataset.price,
          title: plusBtn.dataset.title,
          productTitle: plusBtn.dataset.productTitle,
          variantLabel: plusBtn.dataset.variantLabel,
          img: plusBtn.dataset.img,
        } : null);
        const minusBtn = row.querySelector('.js-modal-minus');
        if (minusBtn) {
          if (newQty === 0) { minusBtn.disabled = true; minusBtn.classList.add('is-disabled'); }
          else { minusBtn.disabled = false; minusBtn.classList.remove('is-disabled'); }
        }
        if (plusBtn) updatePlusButtonState(plusBtn, newQty, effMax);
        updateSubtotal();
      });
    });
  }

  /* ── Variant modal ──────────────────────── */
  async function openVariantModal(productId, stepKey) {
    const modal = $('#variant-modal');
    const content = $('#modal-content');
    const confirmBtn = $('#modal-confirm');
    const pool = stepKey === 'step3' ? state.upsells : state.items;

    modalCloseRefresh = () => {
      const container = $(`#${stepKey === 'step3' ? 'step3' : 'step2'}-products`);
      refreshQtyInGrid(container, productId, pool);
    };

    content.innerHTML = '<p class="fembe-sub__loading">読み込み中...</p>';
    modal.classList.remove('is-hidden');

    let product;
    try {
      const res = await fetch(`/products.json?limit=250`);
      const data = await res.json();
      product = data.products.find(p => p.id === productId);
    } catch (_) {}

    if (!product) {
      content.innerHTML = '<p>商品情報の取得に失敗しました</p>';
      return;
    }

    // option1（カラー等）でグループ化
    const groups = [];
    product.variants.forEach(v => {
      const groupKey = v.option1;
      const img = v.featured_image?.src || product.images.find(i => i.variant_ids.includes(v.id))?.src || product.images[0]?.src || '';
      let g = groups.find(g => g.key === groupKey);
      if (!g) { g = { key: groupKey, img, variants: [] }; groups.push(g); }
      g.variants.push(v);
    });

    const isSingleOption = product.options.length === 1;

    // モーダルタイトル: オプション名をそのまま「・」で結合
    const optionNames = product.options.map(o => o.name).filter(n => n !== 'Title');
    const modalTitle = (optionNames.length > 0 ? optionNames.join('・') : 'バリエーション') + 'を選ぶ';
    const titleEl = $('#modal-title');
    if (titleEl) titleEl.textContent = modalTitle;

    // 単一オプション商品はトップに商品画像を表示
    const singleImg = isSingleOption ? (product.images[0]?.src || '') : '';

    content.innerHTML = `
      ${singleImg ? `
        <div class="fembe-sub__modal-product-top">
          <img src="${singleImg}" alt="${product.title}" class="fembe-sub__modal-group-img">
        </div>` : ''}
      ${groups.map(g => `
        <div class="fembe-sub__modal-group">
          ${!isSingleOption ? `
            <div class="fembe-sub__modal-group-header">
              <img src="${g.img}" alt="${g.key}" class="fembe-sub__modal-group-img">
              <span class="fembe-sub__modal-group-name">${g.key}</span>
            </div>` : ''}
          ${g.variants.map(v => {
            const invData = getInv(v.id, v.available);
            const qty = getQty(v.id, pool);
            const avail = invData.available;
            const max = effectiveMax(inventoryMax(invData.qty, invData.mgmt, invData.policy, avail), v.id, pool);
            const atMax = qty >= max;
            const label = isSingleOption ? v.title : (v.option2 ? `${v.option2}${v.option3 ? ' / ' + v.option3 : ''}` : v.title);
            return `
              <div class="fembe-sub__variant-row ${!avail ? 'is-sold-out' : ''}">
                <div class="fembe-sub__variant-label">
                  <div class="fembe-sub__variant-name-line">
                    <span class="fembe-sub__variant-name">${label}</span>
                    <span class="fembe-sub__variant-sep"> / </span>
                    <span class="${avail ? 'in-stock' : 'out-stock'}">${avail ? '在庫あり' : '在庫なし'}</span>
                  </div>
                  <p class="fembe-sub__variant-price">${fmt(v.price)}</p>
                </div>
                <div class="fembe-sub__qty-row">
                  <button class="fembe-sub__qty-btn js-modal-minus ${!avail || qty === 0 ? 'is-disabled' : ''}"
                    ${!avail || qty === 0 ? 'disabled' : ''}
                    data-variant-id="${v.id}" data-product-id="${productId}">−</button>
                  <input type="number" class="fembe-sub__qty-val fembe-sub__qty-input js-modal-qty-input"
                    value="${qty}" min="0" ${!avail ? 'disabled' : ''}
                    data-variant-id="${v.id}" data-product-id="${productId}"
                    data-inventory="${invData.qty || 0}"
                    data-inv-mgmt="${invData.mgmt || ''}"
                    data-inv-policy="${invData.policy || ''}">
                  <button class="fembe-sub__qty-btn js-modal-plus ${!avail || atMax ? 'is-disabled' : ''}"
                    ${!avail || atMax ? 'disabled' : ''}
                    data-variant-id="${v.id}" data-price="${v.price}"
                    data-product-id="${productId}"
                    data-product-title="${product.title}"
                    data-variant-label="${isSingleOption ? label : (g.key + (label ? ' ' + label : ''))}"
                    data-title="${product.title}${!isSingleOption ? ' ' + g.key : ''} ${label}"
                    data-img="${g.img}"
                    data-inventory="${invData.qty || 0}"
                    data-inv-mgmt="${invData.mgmt || ''}"
                    data-inv-policy="${invData.policy || ''}">＋</button>
                </div>
                <p class="fembe-sub__inventory-msg js-modal-inventory-msg"></p>
              </div>`;
          }).join('')}
        </div>`).join('')}
    `;

    bindModalQtyEvents(content, pool);

    // 確定するボタン
    confirmBtn.textContent = '確定する';
    const newConfirm = confirmBtn.cloneNode(true); // イベント重複防止
    confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);
    newConfirm.textContent = '確定する';
    newConfirm.addEventListener('click', () => {
      closeModal();
    });
  }

  function closeModal() {
    $('#variant-modal').classList.add('is-hidden');
    if (modalCloseRefresh) {
      modalCloseRefresh();
      modalCloseRefresh = null;
    }
  }

  /* ── Group modal (cross-product variants) ── */
  function openGroupModal(groupData, stepKey) {
    const modal = $('#variant-modal');
    const content = $('#modal-content');
    const confirmBtn = $('#modal-confirm');
    const pool = stepKey === 'step3' ? state.upsells : state.items;

    modalCloseRefresh = () => {
      const container = $(`#${stepKey === 'step3' ? 'step3' : 'step2'}-products`);
      const groupCard = container.querySelector(`[data-group-id="${groupData.groupId}"]`);
      if (!groupCard) return;
      const gs = window.FEMBE_VARIANT_GROUPS || {};
      const totalQty = getGroupQty(groupData.groupId, pool, gs);
      const btn = groupCard.querySelector('.js-group-btn');
      if (btn) {
        const optionLabel = btn.dataset.optionLabel || 'バリエーション選択';
        btn.textContent = totalQty > 0 ? `${optionLabel} (${totalQty})` : optionLabel;
      }
    };

    const titleEl = $('#modal-title');
    if (titleEl) {
      const firstProductId = groupData.products[0]?.productId;
      const cachedProduct = productCache[firstProductId];
      const groupOptNames = cachedProduct
        ? (cachedProduct.options || []).map(o => o.name).filter(n => n !== 'Title')
        : [];
      titleEl.textContent = (groupOptNames.length > 0 ? groupOptNames.join('・') : 'バリエーション') + 'を選ぶ';
    }
    modal.classList.remove('is-hidden');

    const repImg = groupData.products[0]?.img || '';
    content.innerHTML = `
      ${repImg ? `
        <div class="fembe-sub__modal-product-top">
          <img src="${repImg}" alt="${groupData.groupName}" class="fembe-sub__modal-group-img">
        </div>` : ''}
      ${groupData.products.map(gp => {
        const qty = getQty(gp.variantId, pool);
        const avail = gp.available;
        const max = effectiveMax(inventoryMax(gp.inventoryQty, gp.inventoryMgmt, gp.inventoryPolicy, avail), gp.variantId, pool);
        const atMax = qty >= max;
        const label = gp.label || gp.title;
        return `
          <div class="fembe-sub__variant-row ${!avail ? 'is-sold-out' : ''}">
            <div class="fembe-sub__variant-label">
              <div class="fembe-sub__variant-name-line">
                <span class="fembe-sub__variant-name">${label}</span>
                <span class="fembe-sub__variant-sep"> / </span>
                <span class="${avail ? 'in-stock' : 'out-stock'}">${avail ? '在庫あり' : '在庫なし'}</span>
              </div>
              <p class="fembe-sub__variant-price">${fmt(gp.price)}</p>
            </div>
            <div class="fembe-sub__qty-row">
              <button class="fembe-sub__qty-btn js-modal-minus ${!avail || qty === 0 ? 'is-disabled' : ''}"
                ${!avail || qty === 0 ? 'disabled' : ''}
                data-variant-id="${gp.variantId}" data-product-id="${gp.productId}">−</button>
              <input type="number" class="fembe-sub__qty-val fembe-sub__qty-input js-modal-qty-input"
                value="${qty}" min="0" ${!avail ? 'disabled' : ''}
                data-variant-id="${gp.variantId}" data-product-id="${gp.productId}"
                data-inventory="${gp.inventoryQty || 0}"
                data-inv-mgmt="${gp.inventoryMgmt || ''}"
                data-inv-policy="${gp.inventoryPolicy || ''}">
              <button class="fembe-sub__qty-btn js-modal-plus ${!avail || atMax ? 'is-disabled' : ''}"
                ${!avail || atMax ? 'disabled' : ''}
                data-variant-id="${gp.variantId}"
                data-product-id="${gp.productId}"
                data-price="${gp.price}"
                data-product-title="${groupData.groupName}"
                data-variant-label="${label}"
                data-title="${groupData.groupName} ${label}"
                data-img="${gp.img}"
                data-inventory="${gp.inventoryQty || 0}"
                data-inv-mgmt="${gp.inventoryMgmt || ''}"
                data-inv-policy="${gp.inventoryPolicy || ''}">＋</button>
            </div>
            <p class="fembe-sub__inventory-msg js-modal-inventory-msg"></p>
          </div>`;
      }).join('')}
    `;

    bindModalQtyEvents(content, pool);

    const newConfirm = confirmBtn.cloneNode(true);
    confirmBtn.parentNode.replaceChild(newConfirm, confirmBtn);
    newConfirm.textContent = '確定する';
    newConfirm.addEventListener('click', () => {
      closeModal();
    });
  }

  /* ── Checkout ───────────────────────────── */
  async function checkout() {
    const btn = $('.js-checkout-btn');
    btn.disabled = true;
    btn.textContent = '処理中...';

    const addonPlanId = state.planId;
    // FemBe本体はfembePlansが定義されていればそのIDを使う（ストアによってグループが分かれる場合）
    const fembePlanId = CFG.fembePlans ? CFG.fembePlans[state.planDays] : addonPlanId;
    const cartItems = [
      { id: CFG.fembeVariantId, quantity: 1, selling_plan: fembePlanId,
        properties: { '_theme': state.theme, '_theme_days': state.planDays } },
      ...allItems().map(i => ({
        id: i.variantId, quantity: i.quantity, selling_plan: addonPlanId
      }))
    ];

    console.log('[FemBe] checkout fembePlanId:', fembePlanId, 'addonPlanId:', addonPlanId, 'items:', cartItems);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cartItems })
      });
      const data = await res.json();
      console.log('[FemBe] cart/add.js response:', data);

      if (data.status >= 400) {
        throw new Error(data.description || 'カートエラー');
      }
      // items配列にstatusが含まれる場合はエラー
      if (data.items && data.items.some && !data.items) {
        throw new Error('カート追加に失敗しました');
      }

      // 開店記念クーポンのみここで適用（初回クーポンはカートページのチェックアウトボタンで適用）
      const cartUrl = CFG.themeSelectCode
        ? `/discount/${encodeURIComponent(CFG.themeSelectCode)}?redirect=${encodeURIComponent('/cart')}`
        : '/cart';
      window.location.href = cartUrl;
    } catch (e) {
      console.error('[FemBe] checkout error:', e);
      alert('エラーが発生しました：' + e.message);
      btn.disabled = false;
      btn.textContent = 'カートに追加する';
    }
  }

  /* ── Event bindings ─────────────────────── */
  function bindEvents() {
    // Theme cards
    $$('.fembe-sub__theme-card').forEach(card => {
      card.addEventListener('click', () => {
        $$('.fembe-sub__theme-card').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        // テーマ変更時は商品キャッシュをリセット（次のSTEPで再取得）
        if (state.theme !== card.dataset.theme) {
          state.theme = card.dataset.theme;
          state.planId = CFG.sellingPlans[state.theme][state.planDays];
          state.step2Loaded = false;
          state.step3Loaded = false;
          state.items = [];
          state.upsells = [];
          updateSubtotal();
        }
      });
    });

    // STEP1の「次へ」ボタン（フッター外）
    $$('.fembe-sub__btn-next').forEach(btn => {
      btn.addEventListener('click', () => goTo(+btn.dataset.next));
    });
    $$('.fembe-sub__btn-back').forEach(btn => {
      btn.addEventListener('click', () => goTo(+btn.dataset.back));
    });

    // Cycle buttons
    $$('.fembe-sub__cycle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('.fembe-sub__cycle-btn').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        state.planDays = +btn.dataset.days;
        state.planId = CFG.sellingPlans[state.theme][state.planDays];
        updateSubtotal();
      });
    });

    // 追従フッターのトグル（残存している場合）
    const toggleBtn = $('.js-toggle-breakdown');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const panel = $('#breakdown-panel');
        const icon = $('.js-toggle-icon');
        const isOpen = !panel.classList.contains('is-hidden');
        panel.classList.toggle('is-hidden', isOpen);
        icon.classList.toggle('is-open', !isOpen);
      });
    }

    // インラインフッターのトグル（イベント委任でSTEP2/3両対応）
    document.addEventListener('click', (e) => {
      const bar = e.target.closest('.js-toggle-inline-breakdown');
      if (!bar) return;
      const footer = bar.closest('.fembe-sub__inline-footer');
      if (!footer) return;
      const panel = footer.querySelector('.js-inline-breakdown');
      const icon = footer.querySelector('.js-inline-toggle-icon');
      if (!panel) return;
      const isOpen = !panel.classList.contains('is-hidden');
      panel.classList.toggle('is-hidden', isOpen);
      if (icon) icon.classList.toggle('is-open', !isOpen);
      requestAnimationFrame(() => { if (updateInlineFooter) updateInlineFooter(); });
    });

    // Variant modal close
    const variantModal = $('#variant-modal');
    variantModal.addEventListener('click', e => { if (e.target === variantModal) closeModal(); });
    $('#modal-close').addEventListener('click', closeModal);

    // カートに追加する（STEP4）
    const checkoutBtn = $('.js-checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', checkout);
    }
  }

  /* ── Sticky footer position ─────────────── */
  function initStickyFooter() {
    const stickyFooter = $('#sticky-footer');
    if (!stickyFooter) return;

    // Shopify Dawnテーマのフッターを取得（複数セレクターで確実に）
    const siteFooter = document.querySelector('#shopify-section-footer')
      || document.querySelector('footer.footer')
      || document.querySelector('.footer')
      || document.querySelector('footer');

    function update() {
      if (stickyFooter.classList.contains('is-hidden')) return;
      if (!siteFooter) return;

      const siteFooterTop = siteFooter.getBoundingClientRect().top;

      if (siteFooterTop < window.innerHeight) {
        // サイトフッターが画面に入ってきたら、その上に止める
        const bottomOffset = window.innerHeight - siteFooterTop;
        stickyFooter.style.bottom = bottomOffset + 'px';
      } else {
        stickyFooter.style.bottom = '0';
      }
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  }

  /* ── Inline footer fixed ───────────────── */
  function initInlineFooter() {
    const siteFooter = document.querySelector('.order-contact-support')
      || document.querySelector('#shopify-section-footer')
      || document.querySelector('footer.footer')
      || document.querySelector('.footer')
      || document.querySelector('footer');

    function update() {
      // 現在表示中のステップのインラインフッターを取得
      const footer = $$('.fembe-sub__inline-footer').find(f =>
        !f.closest('.fembe-sub__step')?.classList.contains('is-hidden')
      );
      if (!footer) return;

      // ステップにpadding-bottomを設定してフッターに隠れないようにする（静的フッターは不要）
      const step = footer.closest('.fembe-sub__step');
      if (step && !footer.classList.contains('fembe-sub__inline-footer--step4')) {
        step.style.paddingBottom = (footer.offsetHeight + 8) + 'px';
      }
      // STEP4の全幅フッターはbottom制御不要
      if (footer.classList.contains('fembe-sub__inline-footer--step4')) return;

      // サイトフッターとの衝突回避（非表示の場合は衝突判定しない）
      if (!siteFooter || siteFooter.offsetParent === null) { footer.style.bottom = '0'; return; }
      const siteFooterTop = siteFooter.getBoundingClientRect().top;
      footer.style.bottom = siteFooterTop < window.innerHeight
        ? (window.innerHeight - siteFooterTop) + 'px'
        : '0';
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
    updateInlineFooter = update;
  }

  /* ── Init ───────────────────────────────── */
  function updateHeaderHeightVar() {
    const h = $('.fembe-sub__header');
    if (h) document.documentElement.style.setProperty('--fembe-header-height', h.offsetHeight + 'px');
  }

  // 外部LPから ?theme=karada|delicate|yoru でテーマ指定された場合、
  // STEP1のテーマ選択を省略してSTEP2へ直接遷移する
  function applyDeepLinkTheme() {
    const theme = new URLSearchParams(window.location.search).get('theme');
    if (!theme || !CFG.sellingPlans || !CFG.sellingPlans[theme]) return false;

    state.theme = theme;
    state.planId = CFG.sellingPlans[theme][state.planDays];
    state.step2Loaded = false;
    state.step3Loaded = false;
    state.items = [];
    state.upsells = [];

    $$('.fembe-sub__theme-card').forEach(c => {
      c.classList.toggle('is-selected', c.dataset.theme === theme);
    });

    goTo(2);
    return true;
  }

  document.addEventListener('DOMContentLoaded', () => {
    bindEvents();
    if (!applyDeepLinkTheme()) {
      updateSubtotal();
      updateFooter(1); // 初期はSTEP1なのでフッター非表示
      const headerLabel = $('#js-header-step-label');
      if (headerLabel) headerLabel.textContent = STEP_LABELS[1] || '';
    }
    updateHeaderHeightVar();
    window.addEventListener('resize', updateHeaderHeightVar, { passive: true });
    initStickyFooter();
    initInlineFooter();
  });

})();
